/**
 * Amazon Cognito Hosted UI integration using the Authorization Code + PKCE
 * flow (no client secret required for a public SPA client). Tokens are kept
 * in sessionStorage only (cleared when the tab closes) -- never written to
 * localStorage or exposed to the service worker cache.
 */
const STORAGE_KEY = "hslh_admin_session";
const VERIFIER_KEY = "hslh_pkce_verifier";

function env(name, fallback = "") {
  return import.meta.env[name] || fallback;
}

function base64UrlEncode(bytes) {
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomBytes(length) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return array;
}

async function sha256(input) {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return new Uint8Array(digest);
}

function redirectUri() {
  return `${window.location.origin}/admin/login`;
}

export async function startLogin() {
  const verifier = base64UrlEncode(randomBytes(32));
  const challenge = base64UrlEncode(await sha256(verifier));
  sessionStorage.setItem(VERIFIER_KEY, verifier);

  const domain = env("VITE_COGNITO_DOMAIN");
  const clientId = env("VITE_COGNITO_CLIENT_ID");
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: "code",
    scope: "openid email profile",
    redirect_uri: redirectUri(),
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  window.location.href = `${domain}/oauth2/authorize?${params.toString()}`;
}

export async function completeLogin(code) {
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const domain = env("VITE_COGNITO_DOMAIN");
  const clientId = env("VITE_COGNITO_CLIENT_ID");

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: clientId,
    code,
    redirect_uri: redirectUri(),
    code_verifier: verifier || "",
  });

  const response = await fetch(`${domain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!response.ok) {
    throw new Error("Sign-in failed. Please try again.");
  }
  const tokens = await response.json();
  const session = {
    idToken: tokens.id_token,
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    expiresAt: Date.now() + (tokens.expires_in || 3600) * 1000,
  };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  sessionStorage.removeItem(VERIFIER_KEY);
  return session;
}

export function getSession() {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const session = JSON.parse(raw);
    if (session.expiresAt < Date.now()) {
      clearSession();
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function clearSession() {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function logout() {
  clearSession();
  const domain = env("VITE_COGNITO_DOMAIN");
  const clientId = env("VITE_COGNITO_CLIENT_ID");
  const params = new URLSearchParams({
    client_id: clientId,
    logout_uri: `${window.location.origin}/admin/login`,
  });
  window.location.href = `${domain}/logout?${params.toString()}`;
}

export function decodeIdTokenClaims(idToken) {
  try {
    const payload = idToken.split(".")[1];
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(normalized));
  } catch {
    return {};
  }
}
